export interface PaletteColor {
  main: string;
  light: string;
  dark: string;
  contrastText: string;
  950: string;
  900: string;
  800: string;
  700: string;
  600: string;
  500: string;
  400: string;
  300: string;
  200: string;
  100: string;
  50: string;
  25: string;
}

const black = '#000000';

const white = '#FFFFFF';

const defaultPrimaryColor = {
  950: '#07111F',
  900: '#0B1B2E',
  800: '#102A43',
  700: '#163A5F',
  600: '#1E4E7A',
  500: '#2D6A9F',
  400: '#4F8FC2',
  300: '#82B6DA',
  200: '#B7D6EA',
  100: '#D8EAF5',
  50: '#EEF7FC',
  25: '#F7FBFE',
};


const defaultSecondaryColor = {
  950: '#211713',
  900: '#34241D',
  800: '#4A3329',
  700: '#654737',
  600: '#815E49',
  500: '#9B7861',
  400: '#B4937D',
  300: '#CAB09F',
  200: '#DDC8BA',
  100: '#EDE0D7',
  50: '#F7F1ED',
  25: '#FBF8F6',
};

const green = {
  950: '#042018',
  900: '#073326',
  800: '#0A4735',
  700: '#0D5F47',
  600: '#0E7A5A',
  500: '#14966F',
  400: '#32B889',
  300: '#6CD3AD',
  200: '#A4E6CB',
  100: '#D3F4E7',
  50: '#ECFBF5',
  25: '#F6FEFA',
};

const gray = {
  950: '#0F1720',
  900: '#182230',
  800: '#243142',
  700: '#334256',
  600: '#4A5A6D',
  500: '#667789',
  400: '#8998A8',
  300: '#AEB8C4',
  200: '#CDD5DF',
  100: '#E4E9EF',
  50: '#F2F5F8',
  25: '#F8FAFC',
};

const error = {
  950: '#4A0D12',
  900: '#71151B',
  800: '#8F1B22',
  700: '#B4232B',
  600: '#D7353E',
  500: '#EF535B',
  400: '#F47C82',
  300: '#F8A8AC',
  200: '#FBCBCD',
  100: '#FDE4E5',
  50: '#FEF2F2',
  25: '#FFFAFA',
};

const warning = {
  950: '#3D2205',
  900: '#663A08',
  800: '#87510B',
  700: '#AC6B10',
  600: '#D48616',
  500: '#EFA826',
  400: '#F5BE4D',
  300: '#F8D27C',
  200: '#FBE4AE',
  100: '#FDF1D2',
  50: '#FFFAEC',
  25: '#FFFCF5',
};

const success = {
  950: '#042018',
  900: '#073326',
  800: '#0A4735',
  700: '#0D5F47',
  600: '#0E7A5A',
  500: '#14966F',
  400: '#32B889',
  300: '#6CD3AD',
  200: '#A4E6CB',
  100: '#D3F4E7',
  50: '#ECFBF5',
  25: '#F6FEFA',
};

const blue = {
  950: '#082342',
  900: '#0C3768',
  800: '#0F4A8A',
  700: '#145EAD',
  600: '#1C76D3',
  500: '#3694EA',
  400: '#63B1F4',
  300: '#94CDF8',
  200: '#BFE3FB',
  100: '#DDF1FD',
  50: '#EFF9FF',
  25: '#F7FCFF',
};

const darkBlue = {
  950: '#050B14',
  900: '#081524',
  800: '#0B2035',
  700: '#0F2D4A',
  600: '#143B60',
  500: '#1F527F',
  400: '#3D75A8',
  300: '#70A1CB',
  200: '#A9C8E2',
  100: '#D5E5F2',
  50: '#EEF6FB',
  25: '#F7FBFD',
};

const violet = {
  950: '#21103F',
  900: '#351A63',
  800: '#482486',
  700: '#5D31A8',
  600: '#7246C7',
  500: '#8B63D9',
  400: '#A989E6',
  300: '#C7B2F0',
  200: '#DED2F7',
  100: '#EEE7FB',
  50: '#F7F3FE',
  25: '#FCFAFF',
};

const yellow = {
  950: '#3A2603',
  900: '#5F4006',
  800: '#80590A',
  700: '#A4770F',
  600: '#C99518',
  500: '#E5B52B',
  400: '#F0C955',
  300: '#F6DA83',
  200: '#FAEAB4',
  100: '#FCF4D8',
  50: '#FEFAEC',
  25: '#FFFDF5',
};

const PrimaryColors = defaultPrimaryColor;
const SecondaryColors = defaultSecondaryColor;

const primary = {
  main: PrimaryColors[600],
  light: PrimaryColors[400],
  dark: PrimaryColors[700],
  ...PrimaryColors,
  contrastText: white,
};

const defaultPrimary = {
  main: PrimaryColors[600],
  light: PrimaryColors[400],
  dark: PrimaryColors[700],
  ...PrimaryColors,
  contrastText: white,
};

const secondary = {
  main: SecondaryColors[600],
  light: SecondaryColors[400],
  dark: SecondaryColors[700],
  ...SecondaryColors,
  contrastText: white,
};

const defaultSecondary = {
  main: SecondaryColors[600],
  light: SecondaryColors[400],
  dark: SecondaryColors[700],
  ...SecondaryColors,
  contrastText: white,
};

export const palette = {
  custom: {
    gray: {
      main: gray[600],
      light: gray[500],
      dark: gray[700],
      contrastText: white,
      ...gray,
    },
    blue: {
      main: blue[600],
      light: blue[500],
      dark: blue[700],
      contrastText: white,
      ...blue,
    },
    darkBlue: {
      main: darkBlue[600],
      light: darkBlue[500],
      dark: darkBlue[700],
      contrastText: white,
      ...darkBlue,
    },
    violet: {
      main: violet[600],
      light: violet[500],
      dark: violet[700],
      contrastText: white,
      ...violet,
    },
    green: {
      main: green[600],
      light: green[500],
      dark: green[700],
      contrastText: white,
      ...green,
    },
    yellow: {
      main: yellow[600],
      light: yellow[500],
      dark: yellow[700],
      contrastText: black,
      ...yellow,
    },
  },
  primary,
  secondary,
  defaultPrimary,
  defaultSecondary,
  error: {
    main: error[600],
    light: error[500],
    dark: error[700],
    contrastText: white,
    ...error,
  },
  warning: {
    main: warning[600],
    light: warning[500],
    dark: warning[700],
    contrastText: black,
    ...warning,
  },
  success: {
    main: success[600],
    light: success[500],
    dark: success[700],
    contrastText: white,
    ...success,
  },
  common: {
    black,
    white,
  },
};